import isEqual from 'react-fast-compare';

import {assert} from 'Asserts';
import {NucleotideType} from 'biojs/BioConstants';
import {BasePrimer} from 'biojs/Sequence';
import {ComponentsSequence} from 'components/Sequence';
import {NonEmptySelection, Selection} from 'molbio/maps/selectables/Selection';
import SequencePrimer from 'molbio/primers/backbone/SequencePrimer';
import {modelEndToRender} from 'molbio/SeqUtil';

const PRIMER_COPY_TEXT = 'The following bases were copied from the sequence primer.';
const SEQUENCE_COPY_TEXT = 'The following bases were copied from the sequence.';
const PCR_COPY_TEXT = 'The following PCR product was copied.';
const OLIGO_COPY_TEXT = 'The following bases were copied from the oligo.';

export enum CopyNucleotideSequenceMode {
  ORIGINAL = 'ORIGINAL',
  DNA = 'DNA',
  DNA_REVERSE_COMPLEMENT = 'DNA_REVERSE_COMPLEMENT',
  TRANSLATION = 'TRANSLATION',
  REVERSE_COMPLEMENT_TRANSLATION = 'REVERSE_COMPLEMENT_TRANSLATION',
  RNA = 'RNA',
  RNA_REVERSE_COMPLEMENT = 'RNA_REVERSE_COMPLEMENT',
  HELM = 'HELM',
}

// Copy modes where the format of the copy is also a nucleotide sequence.
export type NucleotideTargetCopyMode = Exclude<
  CopyNucleotideSequenceMode,
  | CopyNucleotideSequenceMode.TRANSLATION
  | CopyNucleotideSequenceMode.REVERSE_COMPLEMENT_TRANSLATION
  | CopyNucleotideSequenceMode.HELM
>;

export function doesCopyMatchNucleotideType(
  sourceNucleotideType: NucleotideType,
  copyMode: NucleotideTargetCopyMode
): boolean {
  if (copyMode === CopyNucleotideSequenceMode.ORIGINAL) {
    return true;
  } else if (sourceNucleotideType === NucleotideType.DNA) {
    return (
      copyMode === CopyNucleotideSequenceMode.DNA ||
      copyMode === CopyNucleotideSequenceMode.DNA_REVERSE_COMPLEMENT
    );
  } else {
    return (
      copyMode === CopyNucleotideSequenceMode.RNA ||
      copyMode === CopyNucleotideSequenceMode.RNA_REVERSE_COMPLEMENT
    );
  }
}

export function getPrimersForCopy(
  sequence: ComponentsSequence
): [SequencePrimer | null, SequencePrimer | null] {
  const selection = sequence.get('selection');
  if (selection.canPCR()) {
    return [selection.getStartState('primer')!, selection.getEndState('primer')!];
  }
  const selectedPrimers = sequence.sequencePrimers().filter((p) => p.selected());

  if (selectedPrimers.length === 1) {
    const [selectedPrimer] = selectedPrimers;
    if (
      selectedPrimer.start() === selection.start() &&
      modelEndToRender(selectedPrimer.end(), sequence.length()) === selection.end()
    ) {
      return [selectedPrimer, null];
    }
  }
  return [null, null];
}

export function getSingleCopiedPrimer<Primer extends BasePrimer>(
  primers: [Primer | null, Primer | null]
): Primer | null {
  const selectedPrimers = primers.filter((primer) => primer != null);
  if (selectedPrimers.length === 1) {
    return selectedPrimers[0];
  }
  return null;
}

/**
 * Returns whether the selected primers represent a PCR product. This assumes that the input primers were
 * created by getPrimersForCopy, which does not allow selecting multiple primers unless they form a PCR
 * product.
 */
function _isPCRProductCopied(primers: [BasePrimer | null, BasePrimer | null]): boolean {
  return primers.filter((primer) => primer != null).length === 2;
}

export function getCopyText(isOligo: boolean, primers: [BasePrimer | null, BasePrimer | null]): string {
  if (isOligo) {
    assert(
      isEqual(primers, [null, null]),
      'Primer list should be empty when getting copy text for an oligo.'
    );
    return OLIGO_COPY_TEXT;
  } else if (getSingleCopiedPrimer(primers)) {
    return PRIMER_COPY_TEXT;
  } else if (_isPCRProductCopied(primers)) {
    return PCR_COPY_TEXT;
  } else {
    return SEQUENCE_COPY_TEXT;
  }
}

export function getCopyDataForSequenceAndSelection(
  sequence: ComponentsSequence,
  selection: Selection
): {
  start: number;
  end: number;
  selection?: NonEmptySelection;
  source: ComponentsSequence | SequencePrimer;
  helpText: string;
} {
  const primersForCopy = getPrimersForCopy(sequence);
  const singlePrimer = getSingleCopiedPrimer(primersForCopy);

  if (singlePrimer) {
    // Copying contents of primer.
    return {
      start: 0,
      end: singlePrimer.bases().length,
      selection: undefined,
      source: singlePrimer,
      helpText: PRIMER_COPY_TEXT,
    };
  } else if (selection.isNotEmpty()) {
    // Copying sequence selection.
    return {
      start: selection.start(),
      end: selection.end(),
      selection,
      source: sequence,
      helpText: getCopyText(sequence.isOligo(), primersForCopy),
    };
  } else {
    assert(false, 'Expected non-null selection.');
  }
}

export function getDefaultCopyModeForSequence(sequence: ComponentsSequence): CopyNucleotideSequenceMode {
  const {source} = getCopyDataForSequenceAndSelection(sequence, sequence.selection());
  const copyMode = source.canUseFullUTFlexibility()
    ? CopyNucleotideSequenceMode.ORIGINAL
    : CopyNucleotideSequenceMode[source.nucleotideType()];
  return copyMode;
}
